import React from 'react';
import type { ButtonSize, ButtonVariant } from './types';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  loading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
}

const SIZES: Record<ButtonSize, { padding: string; fontSize: string; height: number; gap: number }> = {
  sm: { padding: '6px 12px', fontSize: 'var(--font-size-sm)', height: 32, gap: 6 },
  md: { padding: '8px 16px', fontSize: 'var(--font-size-base)', height: 40, gap: 8 },
  lg: { padding: '12px 20px', fontSize: 'var(--font-size-lg)', height: 48, gap: 8 },
};

const VARIANTS: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    background: 'var(--color-primary)',
    color: 'var(--color-primary-foreground)',
    border: '1px solid var(--color-primary)',
  },
  secondary: {
    background: 'var(--color-secondary)',
    color: 'var(--color-secondary-foreground)',
    border: '1px solid var(--color-border)',
  },
  success: {
    background: 'var(--color-success)',
    color: 'var(--color-success-foreground)',
    border: '1px solid var(--color-success)',
  },
  danger: {
    background: 'var(--color-destructive)',
    color: 'var(--color-destructive-foreground)',
    border: '1px solid var(--color-destructive)',
  },
  outline: {
    background: 'transparent',
    color: 'var(--color-primary)',
    border: '1px solid var(--color-primary)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--color-foreground)',
    border: '1px solid transparent',
  },
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  block = false,
  disabled = false,
  loading = false,
  iconLeft = null,
  iconRight = null,
  type = 'button',
  onClick,
  children,
  style,
  ...rest
}) => {
  const sz = SIZES[size];
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      style={{
        display: block ? 'flex' : 'inline-flex',
        width: block ? '100%' : undefined,
        alignItems: 'center',
        justifyContent: 'center',
        gap: sz.gap,
        padding: sz.padding,
        minHeight: sz.height,
        fontSize: sz.fontSize,
        fontFamily: 'var(--font-family-base)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: 1,
        borderRadius: 'var(--border-radius-sm)',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.6 : 1,
        transition:
          'background var(--transition-base), border-color var(--transition-base), opacity var(--transition-base)',
        whiteSpace: 'nowrap',
        ...VARIANTS[variant],
        ...style,
      }}
      {...rest}
    >
      {loading && <span className="krysalis-btn-spinner" aria-hidden />}
      {!loading && iconLeft}
      {children}
      {!loading && iconRight}
    </button>
  );
};
